<template>
  <div class="sgc-card">
    <div class="sgc-header">
      <div>
        <h5>{{ $t("dashboard.productWiseSgcConsumptionTitle") }}</h5>
        <p>{{ $t("dashboard.productWiseSgcConsumptionSubtitle", { unit: unitLabel }) }}</p>
      </div>

      <div class="sgc-actions">
        <button :class="{ active: selectedMeasure === 'average' }" @click="setMeasure('average')">
          {{ $t("dashboard.average") }}
        </button>

        <button :class="{ active: selectedMeasure === 'median' }" @click="setMeasure('median')">
          {{ $t("dashboard.median") }}
        </button>
      </div>
    </div>

    <div v-if="loading" class="sgc-empty">
      <GridLoading :infoText="$t('dashboard.loading')" />
    </div>

    <div v-else-if="!products.length" class="sgc-empty"></div>

    <div v-else class="sgc-chart-area">
      <div class="sgc-y-axis">
        <span v-for="tick in yTicks" :key="tick">{{ tick }}</span>
      </div>

      <div class="sgc-chart">
        <div v-for="tick in yTicks" :key="'grid-' + tick" class="sgc-grid-line"></div>

        <div v-for="item in tileProducts" :key="item.productType" class="sgc-bar-item">
          <div class="sgc-bar-wrapper">
            <div class="sgc-column-bar" :style="{ height: getBarHeight(item.value) + '%' }">
              <div class="sgc-tooltip">
                <strong>{{ item.productType }}</strong>
                <span>
                  <i></i>
                  {{ formatValue(item.value) }}
                </span>
              </div>
            </div>
          </div>

          <div class="sgc-x-label" :title="item.productType">{{ item.productType }}</div>
        </div>
      </div>
    </div>

    <button
      v-if="sortedProducts.length > 5"
      ref="expandBtn"
      class="show-all"
      tabindex="0"
      @click="openModal"
      @keydown.enter="openModal"
      @keydown.space.prevent="openModal"
    >
      <i class="fa fa-arrows-alt" aria-hidden="true"></i>
      {{ $t("dashboard.showAllProducts", { count: sortedProducts.length }) }}
    </button>

    <div v-if="showModal" class="sgc-modal-backdrop" @click.self="closeModal">
      <div
        ref="modal"
        class="sgc-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sgcModalTitle"
        tabindex="-1"
        @keydown="onModalKeydown"
      >
        <div class="sgc-modal-header">
          <div>
            <h4 id="sgcModalTitle">{{ $t("dashboard.productWiseSgcConsumptionTitle") }}</h4>
            <p>
              {{ $t("dashboard.allProducts", { count: sortedProducts.length }) }} ·
              {{ selectedMeasureLabel }}
            </p>
          </div>

          <div class="sgc-modal-actions">
            <button :class="{ active: selectedMeasure === 'average' }" @click="setMeasure('average')">
              {{ $t("dashboard.average") }}
            </button>

            <button :class="{ active: selectedMeasure === 'median' }" @click="setMeasure('median')">
              {{ $t("dashboard.median") }}
            </button>

            <button class="nav-btn" :disabled="!canGoPrevious" @click="previousModalProducts">
              <i class="fas fa-chevron-left"></i>
            </button>

            <button class="nav-btn" :disabled="!canGoNext" @click="nextModalProducts">
              <i class="fas fa-chevron-right"></i>
            </button>

            <button class="modal-close-btn" :aria-label="$t('popup.close')" @click="closeModal">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>

        <div class="sgc-modal-chart-area">
          <div class="sgc-y-axis modal-y-axis">
            <span v-for="tick in modalYTicks" :key="tick">{{ tick }}</span>
          </div>

          <div class="sgc-modal-chart">
            <div v-for="tick in modalYTicks" :key="'modal-grid-' + tick" class="sgc-grid-line"></div>

            <div v-for="item in modalVisibleProducts" :key="item.productType" class="sgc-modal-bar-item">
              <div class="sgc-modal-bar-wrapper">
                <div class="sgc-column-bar" :style="{ height: getModalBarHeight(item.value) + '%' }">
                  <div class="sgc-tooltip">
                    <strong>{{ item.productType }}</strong>
                    <span>
                      <i></i>
                      {{ formatValue(item.value) }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="sgc-x-label" :title="item.productType">{{ item.productType }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, nextTick } from "vue";
import { DashboardApi } from "@/api";
import GridLoading from "../GridLoading.vue";

type MeasureType = "median" | "average";

const NICE_FRACTIONS = [1, 2, 2.5, 5];

export default defineComponent({
  name: "ProductWiseSgcConsumption",

  components: {
    GridLoading
  },

  setup() {
    const dashboardApi = new DashboardApi();
    return { dashboardApi };
  },

  data() {
    return {
      selectedMeasure: (sessionStorage.getItem("productWiseSgcMeasure") as MeasureType) || "median",

      showModal: false,
      loading: false,

      modalStartIndex: 0,
      modalVisibleCount: 10,

      unit: "Metric" as string,
      // Pre-aggregated by the API: one row per product type with both measures.
      products: [] as { productType: string; median: number; average: number }[]
    };
  },

  async mounted() {
    await this.getProductWiseSgcConsumption();
  },

  computed: {
    unitSymbol(): string {
      return this.unit === "Imperial" ? "yd" : "m";
    },

    unitLabel(): string {
      return this.unitSymbol === "yd"
        ? this.$t("dashboard.unitYards").toString()
        : this.$t("dashboard.unitMetres").toString();
    },

    selectedMeasureLabel(): string {
      return this.selectedMeasure === "average"
        ? this.$t("dashboard.average").toString()
        : this.$t("dashboard.median").toString();
    },

    sortedProducts() {
      // Median/average are already computed (and unit-converted) by the API per
      // product type; just pick the active measure and sort descending.
      return this.products
        .map(p => ({
          productType: p.productType,
          value: this.selectedMeasure === "average" ? p.average : p.median
        }))
        .sort((a, b) => b.value - a.value);
    },

    tileProducts() {
      return this.sortedProducts.slice(0, 5);
    },

    modalVisibleProducts() {
      return this.sortedProducts.slice(this.modalStartIndex, this.modalStartIndex + this.modalVisibleCount);
    },

    canGoPrevious(): boolean {
      return this.modalStartIndex > 0;
    },

    canGoNext(): boolean {
      return this.modalStartIndex + this.modalVisibleCount < this.sortedProducts.length;
    },

    maxValue(): number {
      const values = this.tileProducts.map((i: any) => i.value);
      return values.length ? Math.max(...values) : 1;
    },

    modalMaxValue(): number {
      const values = this.modalVisibleProducts.map((i: any) => i.value);
      return values.length ? Math.max(...values) : 1;
    },

    yMax(): number {
      return this.niceCeil(this.maxValue);
    },

    modalYMax(): number {
      return this.niceCeil(this.modalMaxValue);
    },

    yTicks() {
      return this.createTicks(this.yMax);
    },

    modalYTicks() {
      return this.createTicks(this.modalYMax);
    }
  },

  methods: {
    async getProductWiseSgcConsumption() {
      this.loading = true;

      try {
        const response = await this.dashboardApi.apiDashboardProductWiseSgcConsumptionGet();
        const rows = response.data ?? [];
        this.unit = rows[0]?.unit ?? this.unit;
        this.products = rows.map(d => ({
          productType: d.productType ?? "",
          median: Number(d.median ?? 0),
          average: Number(d.average ?? 0)
        }));
      } catch (error) {
        console.error("Error loading product wise SGC consumption", error);
        this.products = [];
      } finally {
        this.loading = false;
      }
    },

    setMeasure(measure: MeasureType) {
      this.selectedMeasure = measure;
      sessionStorage.setItem("productWiseSgcMeasure", measure);
      // Keep the current modal page — switching measure only re-sorts the same
      // set of products, so the page index stays valid (openModal resets it).
    },

    previousModalProducts() {
      this.modalStartIndex = Math.max(this.modalStartIndex - this.modalVisibleCount, 0);
    },

    nextModalProducts() {
      if (this.canGoNext) {
        this.modalStartIndex += this.modalVisibleCount;
      }
    },

    getBarHeight(value: number) {
      return (value / this.yMax) * 100;
    },

    getModalBarHeight(value: number) {
      return (value / this.modalYMax) * 100;
    },

    // Round an axis bound up to a "nice" number (1, 2, 2.5, 5 × 10^n) so the tallest
    // bar fills the chart area even when the values are small fractions (e.g. 0.05),
    // instead of being squashed against a fixed 0–1 axis.
    niceCeil(value: number) {
      if (!Number.isFinite(value) || value <= 0) return 1;

      const exponent = Math.floor(Math.log10(value));
      const magnitude = Math.pow(10, exponent);
      const fraction = Number((value / magnitude).toFixed(6));

      const niceFraction = NICE_FRACTIONS.find(f => fraction <= f) ?? 10;

      return niceFraction * magnitude;
    },

    createTicks(max: number) {
      const step = max / 5;

      // Show enough decimals for the step size so fractional axes don't collapse
      // to "0.0" (e.g. a 0.01 step needs 2 decimals).
      const decimals = step >= 1 ? 0 : Math.max(1, Math.ceil(-Math.log10(step)));

      return [max, max - step, max - step * 2, max - step * 3, max - step * 4, 0].map(x => Number(x.toFixed(decimals)));
    },

    formatValue(value: number) {
      return `${value.toFixed(1)} ${this.unitSymbol}`;
    },

    async openModal() {
      this.modalStartIndex = 0;
      this.showModal = true;
      document.body.style.overflow = "hidden";

      await nextTick();
      (this.$refs.modal as HTMLElement)?.focus();
    },

    closeModal() {
      this.showModal = false;
      document.body.style.overflow = "";

      // Accessibility: return focus to the control that opened the modal.
      (this.$refs.expandBtn as HTMLElement)?.focus();
    },

    onModalKeydown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        this.closeModal();
        return;
      }

      if (e.key !== "Tab") return;

      // Accessibility: trap focus within the modal while it is open.
      const modal = this.$refs.modal as HTMLElement;
      if (!modal) return;

      const focusable = Array.from(
        modal.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
      ).filter(el => !el.hasAttribute("disabled"));
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (e.shiftKey && (active === first || active === modal)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }
});
</script>

<style scoped>
.sgc-card {
  width: 100%;
  height: 100%;
  min-height: 360px;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 8px;
  padding: 14px 18px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}

.sgc-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 8px;
}

.sgc-header h5 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2;
  color: #00152b;
}

.sgc-header p {
  margin: 2px 0 0;
  font-size: 12px;
  color: #333333;
}

.sgc-actions {
  display: flex;
  align-items: center;
}

.sgc-actions button {
  border: 1px solid #0b66c3;
  background: #0b66c3;
  color: #ffffff;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.sgc-actions button.active {
  background: #003f7d;
  border-color: #003f7d;
}

.sgc-actions button:first-child {
  border-radius: 4px 0 0 4px;
}

.sgc-actions button:nth-child(2) {
  border-radius: 0 4px 4px 0;
  margin-left: -1px;
}

.sgc-empty {
  height: 255px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #777777;
  font-size: 12px;
}

.sgc-chart-area {
  display: grid;
  grid-template-columns: 32px 1fr;
  height: 255px;
  margin-top: 6px;
  flex: 1;
}

.sgc-y-axis {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-bottom: 28px;
  color: #617da3;
  font-size: 11px;
  text-align: right;
}

/* Unit symbol annotation sits above the axis (out of the tick flow, so it does
   not affect tick spacing). Follows the tenant unit configuration. */
.sgc-y-unit {
  position: absolute;
  top: -13px;
  right: 0;
  font-weight: 700;
  color: #00152b;
}

.sgc-chart {
  position: relative;
  height: 100%;
  min-width: 0;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  grid-template-rows: 100%;
  align-items: end;
  padding: 0 24px 0;
}

.sgc-chart::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: 28px;
  height: 1px;
  background: #c8d8ea;
}

.sgc-grid-line {
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  background: #c8d8ea;
}

/* Gridlines must sit inside the plot area (chart height minus the x-label gutter
   at the bottom: 28px for the tile, 42px for the modal) so they line up with the
   bars and the y-axis labels. Spreading them over the full height left the bottom
   gap (0 → first tick) squashed against the baseline. */
.sgc-chart .sgc-grid-line:nth-child(1),
.sgc-modal-chart .sgc-grid-line:nth-child(1) {
  top: 0;
}

.sgc-chart .sgc-grid-line:nth-child(2) {
  top: calc((100% - 28px) * 0.2);
}

.sgc-chart .sgc-grid-line:nth-child(3) {
  top: calc((100% - 28px) * 0.4);
}

.sgc-chart .sgc-grid-line:nth-child(4) {
  top: calc((100% - 28px) * 0.6);
}

.sgc-chart .sgc-grid-line:nth-child(5) {
  top: calc((100% - 28px) * 0.8);
}

.sgc-modal-chart .sgc-grid-line:nth-child(2) {
  top: calc((100% - 42px) * 0.2);
}

.sgc-modal-chart .sgc-grid-line:nth-child(3) {
  top: calc((100% - 42px) * 0.4);
}

.sgc-modal-chart .sgc-grid-line:nth-child(4) {
  top: calc((100% - 42px) * 0.6);
}

.sgc-modal-chart .sgc-grid-line:nth-child(5) {
  top: calc((100% - 42px) * 0.8);
}

.sgc-grid-line:nth-child(6) {
  display: none;
}

.sgc-bar-item {
  position: relative;
  z-index: 2;
  flex: 1 1 0;
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
}

.sgc-bar-wrapper {
  width: 32px;
  height: calc(100% - 28px);
  display: flex;
  align-items: flex-end;
}

.sgc-column-bar {
  position: relative;
  width: 100%;
  background: #367bb9;
  border-radius: 3px 3px 0 0;
  min-height: 4px;
  transition: height 0.25s ease;
}

.sgc-x-label {
  width: 100%;
  box-sizing: border-box;
  padding: 0 4px;
  height: 22px;
  margin-top: 6px;
  font-size: 11px;
  color: #333333;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sgc-bar-item:hover,
.sgc-modal-bar-item:hover {
  z-index: 10;
}

.show-all {
  border: 0;
  border-top: 1px dashed #d2d8df;
  margin-top: 0;
  padding: 6px 18px 10px;
  background: #ffffff;
  color: #0054a6;
  font-size: 13px;
  font-family: inherit;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  cursor: pointer;
  width: 100%;
  text-align: left;
}

.show-all i {
  font-size: 12px;
}

.sgc-tooltip {
  display: none;
  position: absolute;
  bottom: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
  background: #0054a6;
  color: #ffffff;
  padding: 8px 10px;
  border-radius: 5px;
  width: max-content;
  min-width: 105px;
  max-width: 220px;
  text-align: left;
  font-size: 12px;
  z-index: 20;
  white-space: normal;
  overflow-wrap: break-word;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.18);
}

.sgc-column-bar::after {
  content: "";
  display: none;
  position: absolute;
  left: 50%;
  bottom: calc(100% + 3px);
  transform: translateX(-50%);
  border-left: 7px solid transparent;
  border-right: 7px solid transparent;
  border-top: 7px solid #0054a6;
  z-index: 21;
  pointer-events: none;
}

.sgc-column-bar:hover::after {
  display: block;
}

.sgc-bar-item:first-child .sgc-tooltip,
.sgc-modal-bar-item:first-child .sgc-tooltip {
  left: 0;
  right: auto;
  transform: none;
}

.sgc-bar-item:last-child .sgc-tooltip,
.sgc-modal-bar-item:last-child .sgc-tooltip {
  left: auto;
  right: -40px;
  transform: none;
}

.sgc-tooltip strong {
  display: block;
  font-weight: 700;
  margin-bottom: 5px;
}

.sgc-tooltip span {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.sgc-tooltip span i {
  width: 11px;
  height: 11px;
  border: 2px solid #ffffff;
  display: inline-block;
}

.sgc-column-bar:hover .sgc-tooltip {
  display: block;
}

/* MODAL */

.sgc-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 12000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.sgc-modal {
  width: 92vw;
  max-width: 1600px;
  height: 88vh;
  max-height: 92vh;
  background: #ffffff;
  border-radius: 10px;
  overflow: hidden;
  outline: none;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.sgc-modal-header {
  height: 78px;
  padding: 22px 28px 12px;
  border-bottom: 1px solid #e5eaf0;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.sgc-modal-header h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #00152b;
}

.sgc-modal-header p {
  margin: 6px 0 0;
  font-size: 13px;
  color: #333333;
}

.sgc-modal-actions {
  display: flex;
  align-items: center;
  gap: 0;
}

.sgc-modal-actions button {
  border: 1px solid #0b66c3;
  background: #0b66c3;
  color: #ffffff;
  height: 34px;
  padding: 0 14px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.sgc-modal-actions button.active {
  background: #003f7d;
  border-color: #003f7d;
}

.sgc-modal-actions button:first-child {
  border-radius: 4px 0 0 4px;
}

.sgc-modal-actions button:nth-child(2) {
  border-radius: 0 4px 4px 0;
}

.nav-btn {
  width: 30px;
  height: 30px;
  border: none !important;
  background: transparent !important;
  color: #6e87aa !important;
  border-radius: 4px;
  font-size: 18px !important;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 !important;
  cursor: pointer;
  margin-left: 10px;
}

.nav-btn + .nav-btn {
  margin-left: 2px;
}

.nav-btn:hover {
  background: #f3f6fa !important;
}

.nav-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.nav-btn:disabled:hover {
  background: transparent !important;
}

.modal-close-btn {
  width: 30px;
  height: 30px;
  border: none !important;
  background: transparent !important;
  color: #6e87aa !important;
  border-radius: 4px;
  font-size: 22px !important;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 !important;
  cursor: pointer;
  margin-left: 8px;
}

.modal-close-btn:hover {
  background: #f3f6fa !important;
}

.sgc-modal-chart-area {
  flex: 1 1 auto;
  min-height: 0;
  display: grid;
  grid-template-columns: 48px 1fr;
  padding: 16px 34px 32px 28px;
}

.modal-y-axis {
  font-size: 13px;
  padding-bottom: 42px;
}

.sgc-modal-chart {
  position: relative;
  height: 100%;
  min-width: 0;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  grid-template-rows: 100%;
  align-items: end;
  padding: 0 0px 15px;
}

.sgc-modal-chart::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: 42px;
  height: 1px;
  background: #c8d8ea;
}

.sgc-modal-bar-item {
  position: relative;
  z-index: 2;
  flex: 1 1 0;
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
}

.sgc-modal-bar-wrapper {
  width: 40px;
  height: calc(100% - 42px);
  display: flex;
  align-items: flex-end;
}
</style>
